import { createContext, useContext } from 'react'
import type { PanelState, PanelId } from 'state/PanelState'
import type { RefreshSource } from 'lib/refreshRedesign'
import {
    isPanelLeft as _isPanelLeft,
    isPanelCenter as _isPanelCenter,
    isPanelRight as _isPanelRight,
} from 'state/imperativeApis/swiperApi'
import useStore from 'state/knovStore'

const StaticPanelContext = createContext<{
    panelId: PanelId
}>({
    panelId: undefined,
})

export const StaticPanelContextProvider = StaticPanelContext.Provider

export function useStaticPanelContext() {
    const context = useContext(StaticPanelContext)
    if (context === undefined) {
        throw new Error('useStaticPanelContext must be used within a StaticPanelContextProvider')
    }
    return context
}

const PanelContext = createContext<{
    panel: PanelState
    panelId: PanelId
    panelIndex?: number
    filter: PanelState['filter']
    hide: boolean
    panelContentActive?: boolean
    panelContentPrewarming?: boolean
    isCenter?: () => boolean // Add isCenter function type
    panelContainerRef: React.RefObject<HTMLDivElement>
    scrollContainerRef: React.RefObject<HTMLDivElement>
    revealPendingOpenPlacement?: (
        questId: string | number,
        answerId: string | number,
        intent?: PanelState['pendingOpenPlacement'],
    ) => void
    failPendingOpenPlacement?: (
        questId: string | number,
        answerId: string | number,
        intent: PanelState['pendingOpenPlacement'],
        reason?: 'timeout' | 'unavailable' | 'error',
    ) => void
    // Posting hands the panel the new reply; the panel decides the feedback.
    offerAnswerJump?: (
        answerId?: string | null,
        questId?: string | null,
        options?: { fromBottomEditor?: boolean; viaModal?: boolean },
    ) => void
    setExternalHideBottomChip?: React.Dispatch<React.SetStateAction<boolean>>
    setBottomEditorMounted?: (mounted: boolean) => void
    // Where a thread portals its bottom editor: fixed to the panel's foot.
    bottomEditorHost?: HTMLDivElement | null
    setIsRefreshing?: React.Dispatch<React.SetStateAction<boolean>>
    isRefreshing?: boolean
    setIsInitialLoading?: React.Dispatch<React.SetStateAction<boolean>>
    isInitialLoading?: boolean
    // Source of the in-flight refresh drives which feedback renders: gesture →
    // pull indicator; initial/nav/search → header halo.
    refreshSource?: RefreshSource | null
}>({
    panel: undefined,
    panelId: undefined,
    panelIndex: undefined,
    filter: undefined,
    hide: undefined,
    panelContentActive: true,
    panelContentPrewarming: false,
    isCenter: undefined, // Initialize isCenter as undefined
    panelContainerRef: undefined,
    scrollContainerRef: undefined,
    revealPendingOpenPlacement: undefined,
    offerAnswerJump: undefined,
    setExternalHideBottomChip: undefined,
    setBottomEditorMounted: undefined,
    bottomEditorHost: null,
    setIsRefreshing: undefined,
    isRefreshing: undefined,
    setIsInitialLoading: undefined,
    isInitialLoading: false,
    refreshSource: undefined,
})

export const PanelContextProvider = PanelContext.Provider

export default function usePanelContext() {
    const context = useContext(PanelContext)
    const panelId = context.panelId

    const isPanelLeft = () => _isPanelLeft(panelId)

    const isPanelCenter = () => _isPanelCenter(panelId)

    const isPanelRight = () => _isPanelRight(panelId)

    const panelToLeft = () => {
        const panels = useStore.getState().panels
        const panelIx = panels.getIndexById(panelId)
        return panels.state[panelIx - 1]
    }

    const panelToRight = () => {
        const panels = useStore.getState().panels
        const panelIx = panels.getIndexById(panelId)
        return panels.state[panelIx + 1]
    }

    return {
        ...context,
        isPanelLeft,
        isPanelCenter,
        isPanelRight,
        panelToLeft,
        panelToRight,
    }
}
